import type { ApiClient } from './client';
import type { PaginationMeta } from '../../types/discoveryBadges';

export interface FavoriteState {
  is_favorited: boolean;
  favorite_count: number;
}

export interface FavoriteVehicleCard {
  id: number;
  year: number;
  make: string;
  model: string;
  trim?: string;
  nickname?: string | null;
  display_name?: string;
  owner_username?: string;
  owner_display_name?: string;
  public_slug?: string | null;
  image_url?: string | null;
  modification_count?: number;
  favorite_count?: number;
  is_favorited?: boolean;
  view_count?: number;
  created_at?: string;
  updated_at?: string;
  is_public?: boolean;
  [key: string]: unknown;
}

export interface ListFavoritesParams {
  page?: number;
  limit?: number;
  search?: string;
  sort?: 'recent' | 'updated';
}

export interface RecordVehicleViewParams {
  vehicle_id: number;
  session_token: string;
  source?: string | null;
}

export const favoritesMethods = {
  async favoriteVehicle(vehicleId: number): Promise<FavoriteState> {
    if (!this.isAuthenticated()) {
      throw new Error('Authentication required to favorite builds');
    }
    const response = await this.makeRequest<FavoriteState>('/vehicle_favorite.php', {
      method: 'POST',
      body: JSON.stringify({ vehicle_id: vehicleId }),
      headers: this.getAuthHeaders(),
    });
    if (!response.success) {
      throw new Error(response.message || response.error || 'Failed to add favorite');
    }
    return {
      is_favorited: Boolean(response.data?.is_favorited ?? true),
      favorite_count: Number(response.data?.favorite_count ?? 0),
    };
  },

  async unfavoriteVehicle(vehicleId: number): Promise<FavoriteState> {
    if (!this.isAuthenticated()) {
      throw new Error('Authentication required to update favorites');
    }
    const response = await this.makeRequest<FavoriteState>('/vehicle_favorite.php', {
      method: 'POST',
      body: JSON.stringify({ vehicle_id: vehicleId, action: 'remove' }),
      headers: this.getAuthHeaders(),
    });
    if (!response.success) {
      throw new Error(response.message || response.error || 'Failed to remove favorite');
    }
    return {
      is_favorited: Boolean(response.data?.is_favorited ?? false),
      favorite_count: Number(response.data?.favorite_count ?? 0),
    };
  },

  async getFavoriteState(vehicleId: number): Promise<FavoriteState> {
    const response = await this.makeRequest<FavoriteState>(
      `/vehicle_favorite.php?vehicle_id=${encodeURIComponent(String(vehicleId))}`,
      {
        method: 'GET',
        headers: this.getAuthHeaders(),
      }
    );
    if (!response.success) {
      throw new Error(response.message || response.error || 'Failed to load favorite state');
    }
    return {
      is_favorited: Boolean(response.data?.is_favorited ?? false),
      favorite_count: Number(response.data?.favorite_count ?? 0),
    };
  },

  async listFavorites(
    params: ListFavoritesParams = {}
  ): Promise<{ data: FavoriteVehicleCard[]; pagination: PaginationMeta }> {
    if (!this.isAuthenticated()) {
      throw new Error('Authentication required to list favorites');
    }
    const qs = new URLSearchParams();
    if (params.page) qs.set('page', String(params.page));
    if (params.limit) qs.set('limit', String(params.limit));
    if (params.search) qs.set('search', params.search);
    if (params.sort) qs.set('sort', params.sort);
    const query = qs.toString();
    const response = await this.makeRequest<FavoriteVehicleCard[]>(
      `/vehicle_favorites.php${query ? `?${query}` : ''}`,
      { headers: this.getAuthHeaders() }
    );
    if (!response.success) {
      throw new Error(response.message || response.error || 'Failed to load favorites');
    }
    const pagination =
      (response as { pagination?: PaginationMeta }).pagination || {
        page: params.page || 1,
        limit: params.limit || 20,
        total_count: Array.isArray(response.data) ? response.data.length : 0,
        total_pages: 1,
      };
    return {
      data: Array.isArray(response.data) ? response.data : [],
      pagination,
    };
  },

  async recordVehicleView(payload: RecordVehicleViewParams): Promise<{
    ok: boolean;
    counted: boolean;
    view_count: number | null;
  }> {
    try {
      const response = await this.makeRequest<{ counted?: boolean; view_count?: number }>(
        '/vehicle_view.php',
        {
          method: 'POST',
          body: JSON.stringify({
            vehicle_id: payload.vehicle_id,
            session_token: payload.session_token,
            ...(payload.source ? { source: payload.source } : {}),
          }),
          headers: this.getAuthHeaders(),
        }
      );
      const raw = response.data?.view_count;
      const viewCount =
        typeof raw === 'number' && Number.isFinite(raw) ? Math.max(0, Math.floor(raw)) : null;
      return {
        ok: true,
        counted: Boolean(response.data?.counted),
        view_count: viewCount,
      };
    } catch (error) {
      console.debug('recordVehicleView failed:', error);
      // Never return view_count: 0 on failure — that would wipe a correct UI count.
      return { ok: false, counted: false, view_count: null };
    }
  },
} satisfies ThisType<ApiClient>;
